import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../services/axiosInstance';
import { API_ENDPOINTS } from '../config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AttendanceSlot {
  id: number;
  batch_id: number;
  name: string;
  start_time: string | null;
  end_time: string | null;
  sort_order: number;
  is_active: boolean;
  batch?: { id: number; identifier: string };
}

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'half_day';

export interface AttendanceGridRow {
  employee_id: number;
  name: string;
  status: AttendanceStatus | null;
  notes: string | null;
  marked_by: { id: number; name: string } | null;
  record_id: number | null;
}

export interface AttendanceGrid {
  slot: AttendanceSlot;
  date: string;
  grid: AttendanceGridRow[];
}

// ─── Slots ────────────────────────────────────────────────────────────────────

export const useAttendanceSlots = (params?: { batch_id?: number | string; active?: boolean }) =>
  useQuery<{ data: AttendanceSlot[] }>({
    queryKey: ['attendanceSlots', params],
    queryFn: async () => (await axiosInstance.get(API_ENDPOINTS.ATTENDANCE.SLOTS, { params })).data,
  });

export const useCreateAttendanceSlot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<AttendanceSlot>) =>
      axiosInstance.post(API_ENDPOINTS.ATTENDANCE.SLOTS, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendanceSlots'] }),
  });
};

export const useUpdateAttendanceSlot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<AttendanceSlot> & { id: number }) =>
      axiosInstance.put(API_ENDPOINTS.ATTENDANCE.SLOT(id), data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendanceSlots'] }),
  });
};

export const useDeleteAttendanceSlot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      axiosInstance.delete(API_ENDPOINTS.ATTENDANCE.SLOT(id)).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendanceSlots'] }),
  });
};

// ─── Attendance ───────────────────────────────────────────────────────────────

export const useAttendanceGrid = (slotId?: number | string, date?: string) =>
  useQuery<AttendanceGrid>({
    queryKey: ['attendanceGrid', slotId, date],
    queryFn: async () =>
      (
        await axiosInstance.get(API_ENDPOINTS.ATTENDANCE.LIST, {
          params: { slot_id: slotId, date },
        })
      ).data,
    enabled: !!slotId && !!date,
  });

export const useBulkMarkAttendance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      slot_id: number;
      date: string;
      records: { employee_id: number; status: AttendanceStatus; notes?: string }[];
    }) => axiosInstance.post(API_ENDPOINTS.ATTENDANCE.BULK, data).then((r) => r.data),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['attendanceGrid', vars.slot_id, vars.date] }),
  });
};

export const useUpdateAttendanceRecord = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; status?: AttendanceStatus; notes?: string }) =>
      axiosInstance.patch(API_ENDPOINTS.ATTENDANCE.RECORD(id), data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendanceGrid'] }),
  });
};

export interface RosterSlot {
  slot: { id: number; name: string; start_time: string | null; end_time: string | null };
  employees: { id: number; name: string }[];
}

export const useRoster = (batchId?: number | string, date?: string) =>
  useQuery<{ has_roster: boolean; data: RosterSlot[] }>({
    queryKey: ['roster', batchId, date],
    queryFn: async () =>
      (
        await axiosInstance.get(API_ENDPOINTS.ATTENDANCE.ROSTER, {
          params: { batch_id: batchId, date },
        })
      ).data,
    enabled: !!batchId && !!date,
  });

export const useGenerateRoster = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { batch_id: number | string; date: string }) =>
      axiosInstance.post(API_ENDPOINTS.ATTENDANCE.ROSTER_GENERATE, data).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['roster', vars.batch_id, vars.date] });
      qc.invalidateQueries({ queryKey: ['attendanceGrid'] });
    },
  });
};

export const useAttendanceSummary = (
  batchId?: number | string,
  dateFrom?: string,
  dateTo?: string
) =>
  useQuery({
    queryKey: ['attendanceSummary', batchId, dateFrom, dateTo],
    queryFn: async () =>
      (
        await axiosInstance.get(API_ENDPOINTS.ATTENDANCE.SUMMARY, {
          params: { batch_id: batchId, date_from: dateFrom, date_to: dateTo },
        })
      ).data,
    enabled: !!batchId && !!dateFrom && !!dateTo,
  });
